/**
 * Symptom Logs Hook
 *
 * Tracks symptoms using the patients API.
 * Simple toggle: symptom name + date + optional notes.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { createBatchRetryLedger } from "../batch-create";
import {
  buildSymptomCreateItems,
  settleSymptomCreateBatch,
} from "../symptom-batch";
import { useApiClient } from "../../api/louise-api";
import { calendarKeys, symptomKeys } from "../patient-cache-keys";
import { throwApiError } from "../../api/api-error";

export { symptomKeys };

// ============================================================================
// Types (matches patients API symptoms table)
// ============================================================================

export interface Symptom {
  id: string;
  patientId: string;
  symptom: string;
  dateAndTime: string;
  notes?: string | null;
  createdAt: string;
}

export interface CreateSymptomPayload {
  symptom: string;
  dateAndTime: string;
  notes?: string | null;
}

// ============================================================================
// Common Symptom Types
// ============================================================================

export const COMMON_SYMPTOMS = [
  "Headache",
  "Nausea",
  "Fatigue",
  "Bloating",
  "Cramps",
  "Back pain",
  "Breast tenderness",
  "Mood swings",
  "Anxiety",
  "Insomnia",
  "Hot flashes",
  "Dizziness",
  "Appetite changes",
  "Acne",
  "Joint pain",
] as const;

export type CommonSymptom = (typeof COMMON_SYMPTOMS)[number];

// ============================================================================
// Hooks
// ============================================================================

export interface UseSymptomLogsOptions {
  startDate?: string;
  endDate?: string;
  limit?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch symptoms from the patients API.
 */
export function useSymptomLogs(options: UseSymptomLogsOptions = {}) {
  const { startDate, endDate, limit, enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();

  return useQuery({
    queryKey: symptomKeys.list(
      tenantId ?? "",
      patientId ?? "",
      startDate,
      endDate,
      limit,
    ),
    queryFn: async () => {
      if (tenantId && patientId) {
        try {
          const query: { date?: string; startDate?: string; endDate?: string } =
            {};
          if (startDate && endDate && startDate === endDate) {
            query.date = startDate;
          } else {
            query.startDate = startDate;
            query.endDate = endDate;
          }
          const response = await client.GET(
            "/{tenantId}/patients/{patientId}/symptoms",
            {
              params: {
                path: { tenantId, patientId },
                query,
              },
            },
          );

          if (response.error) {
            throwApiError(
              response.error,
              "Failed to load symptom logs",
              "symptom-logs",
              response.response.status,
            );
          }

          let data = (response.data?.data ?? []) as Symptom[];
          if (limit) {
            data = data.slice(0, limit);
          }
          return data;
        } catch (error) {
          throwApiError(
            error,
            "Failed to load symptom logs",
            "symptom-logs",
            undefined,
          );
        }
      }

      return [];
    },
    enabled: enabled && !!tenantId && !!patientId,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to log a symptom.
 */
export function useLogSymptom() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientSymptomKey = symptomKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (payload: CreateSymptomPayload) => {
      if (!tenantId || !patientId) {
        throw new Error("No tenant or patient ID");
      }

      const response = await client.POST(
        "/{tenantId}/patients/{patientId}/symptoms",
        {
          params: { path: { tenantId, patientId } },
          body: payload,
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to log symptom",
          "useLogSymptom",
          response.response.status,
        );
      }

      return response.data?.data as Symptom;
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: patientSymptomKey });
      await queryClient.cancelQueries({ queryKey: patientCalendarKey });

      const previousData = queryClient.getQueriesData<Symptom[]>({
        queryKey: patientSymptomKey,
      });

      const tempId = `temp-symptom-${Date.now()}`;
      const optimisticSymptom: Symptom = {
        id: tempId,
        patientId: "optimistic",
        symptom: payload.symptom,
        dateAndTime: payload.dateAndTime,
        notes: payload.notes,
        createdAt: new Date().toISOString(),
      };

      queryClient.setQueriesData<Symptom[]>(
        { queryKey: patientSymptomKey },
        (old) => {
          if (!old) return [optimisticSymptom];
          return [optimisticSymptom, ...old];
        },
      );

      return { previousData };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientSymptomKey });
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
    },
  });
}

/**
 * Hook to log multiple symptoms at once.
 *
 * Note: Backend stores 1 record per symptom, each with its own note field.
 * TODO: UI wants 1 day = symptoms[] + shared note - will be fixed later.
 */
export function useLogSymptoms() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientSymptomKey = symptomKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const ledger = useRef(createBatchRetryLedger<Symptom>());

  return useMutation({
    mutationFn: async ({
      symptoms,
      loggedAt,
    }: {
      symptoms: {
        symptom: string;
        notes?: string;
      }[];
      loggedAt?: string;
    }) => {
      const timestamp = loggedAt ?? new Date().toISOString();

      if (!tenantId || !patientId) {
        throw new Error("No tenant or patient ID");
      }

      // Settle per symptom: one failed row must not discard rows the server
      // already accepted, and the retry window keeps only failures (#2236).
      // The key intentionally excludes the volatile timestamp so a retry of
      // an un-dated log doesn't re-create accepted rows.
      const items = buildSymptomCreateItems(
        loggedAt ?? "automatic",
        symptoms.map((symptom) => ({
          symptom: symptom.symptom,
          dateAndTime: timestamp,
          notes: symptom.notes,
        })),
      );

      return settleSymptomCreateBatch(
        items,
        async (body) => {
          const response = await client.POST(
            "/{tenantId}/patients/{patientId}/symptoms",
            {
              params: { path: { tenantId, patientId } },
              body,
            },
          );
          if (response.error) {
            throwApiError(
              response.error,
              "Failed to log symptom",
              "useLogSymptoms",
              response.response.status,
            );
          }
          return response.data?.data as Symptom;
        },
        ledger.current,
        (outcome) =>
          `Failed to log ${outcome.failed.length} symptom(s); ${outcome.succeeded.length} saved`,
      );
    },
    onMutate: async ({ symptoms, loggedAt }) => {
      await queryClient.cancelQueries({ queryKey: patientSymptomKey });
      await queryClient.cancelQueries({ queryKey: patientCalendarKey });

      const previousData = queryClient.getQueriesData<Symptom[]>({
        queryKey: patientSymptomKey,
      });

      const timestamp = loggedAt ?? new Date().toISOString();
      const optimisticSymptoms: Symptom[] = symptoms.map((s, i) => ({
        id: `temp-symptom-batch-${Date.now()}-${i}`,
        patientId: "optimistic",
        symptom: s.symptom,
        dateAndTime: timestamp,
        notes: s.notes,
        createdAt: new Date().toISOString(),
      }));

      queryClient.setQueriesData<Symptom[]>(
        { queryKey: patientSymptomKey },
        (old) => {
          if (!old) return optimisticSymptoms;
          return [...optimisticSymptoms, ...old];
        },
      );

      return { previousData };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientSymptomKey });
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
    },
  });
}

/**
 * Hook to delete a symptom.
 */
export function useDeleteSymptomLog() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const patientSymptomKey = symptomKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );
  const patientCalendarKey = calendarKeys.patient(
    tenantId ?? "",
    patientId ?? "",
  );

  return useMutation({
    mutationFn: async (symptomId: string) => {
      if (!tenantId || !patientId) {
        throw new Error("No tenant or patient ID");
      }

      const response = await client.DELETE(
        "/{tenantId}/patients/{patientId}/symptoms/{symptomId}",
        {
          params: { path: { tenantId, patientId, symptomId } },
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to delete symptom",
          "useDeleteSymptomLog",
          response.response.status,
        );
      }

      return { success: true };
    },
    onMutate: async (symptomId) => {
      await queryClient.cancelQueries({ queryKey: patientSymptomKey });
      await queryClient.cancelQueries({ queryKey: patientCalendarKey });

      const previousData = queryClient.getQueriesData<Symptom[]>({
        queryKey: patientSymptomKey,
      });

      queryClient.setQueriesData<Symptom[]>(
        { queryKey: patientSymptomKey },
        (old) => {
          if (!old) return old;
          return old.filter((s) => s.id !== symptomId);
        },
      );

      return { previousData };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientSymptomKey });
      queryClient.invalidateQueries({ queryKey: patientCalendarKey });
    },
  });
}

// ============================================================================
// Legacy type aliases for backwards compatibility
// ============================================================================

/** @deprecated Use Symptom instead */
export type SymptomLog = Symptom;

/** @deprecated Use CreateSymptomPayload instead */
export interface CreateSymptomLogPayload extends CreateSymptomPayload {
  /** @deprecated Use symptom instead */
  symptomType?: string;
}

/** @deprecated Use symptomKeys instead */
export const symptomLogKeys = symptomKeys;
